"use client";
import React, { useState } from "react";
import { MobileHeader } from "../MobileHeader";
import { TabBar } from "../TabBar";
import { Link, useNavigate } from "react-router-dom";
import ToastNotification from "../ToastNotification";
import PasswordSecurityScreen from "./PasswordSecurityScreen";

interface NotificationSetting {
  id: string;
  title: string;
  description: string;
  enabled: boolean;
}

// Create new screens for measurement units and language
const MeasurementUnitsScreen: React.FC = () => {
  const navigate = useNavigate();
  const [selectedUnit, setSelectedUnit] = useState<string>("liters");
  const [toast, setToast] = useState<{type: 'success' | 'info' | 'warning' | 'error', message: string} | null>(null);
  
  const units = [
    { id: "liters", name: "Liters", description: "Metric measurement (L)" },
    { id: "gallons", name: "Gallons", description: "Imperial measurement (gal)" },
    { id: "ounces", name: "Fluid Ounces", description: "US measurement (fl oz)" }
  ];
  
  const saveSelection = () => {
    setToast({ type: 'success', message: 'Measurement unit updated successfully!' });
    setTimeout(() => navigate('/settings'), 1500);
  };
  
  return (
    <main className="pb-72 mx-auto w-full max-w-[480px]">
      <div className="overflow-hidden w-full bg-white">
        <MobileHeader />
        <section className="pt-8 pb-12 w-full">
          <header className="w-full text-xl font-semibold leading-none text-gray-900">
            <div className="px-4 w-full">
              <div className="w-full">
                <div className="flex justify-between items-center">
                  <h1 className="w-full flex items-center">
                    <button onClick={() => navigate('/settings')} className="mr-2">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    Measurement Units
                  </h1>
                </div>
              </div>
            </div>
          </header>

          <section className="mt-8 w-full">
            <div className="px-4 w-full">
              <p className="text-sm text-gray-600 mb-6">
                Choose your preferred water measurement unit. This will be used throughout the app to display your water usage.
              </p>
              
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-200 mb-8">
                {units.map(unit => (
                  <div 
                    key={unit.id}
                    className="p-4 flex items-center cursor-pointer"
                    onClick={() => setSelectedUnit(unit.id)}
                  >
                    <div className="flex-1">
                      <h3 className="text-base font-medium text-gray-900">{unit.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">{unit.description}</p>
                    </div>
                    <div className="ml-4 flex-shrink-0">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${selectedUnit === unit.id ? 'border-blue-600' : 'border-gray-300'}`}>
                        {selectedUnit === unit.id && (
                          <div className="w-3 h-3 rounded-full bg-blue-600"></div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <button 
                onClick={saveSelection}
                className="w-full py-3 font-medium rounded-lg shadow-sm bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              >
                Save Preference
              </button>
            </div>
          </section>
        </section>
      </div>

      <TabBar />
      
      {/* Toast notification */}
      {toast && (
        <ToastNotification
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </main>
  );
};

const LanguageScreen: React.FC = () => {
  const navigate = useNavigate();
  const [selectedLanguage, setSelectedLanguage] = useState<string>("english");
  const [toast, setToast] = useState<{type: 'success' | 'info' | 'warning' | 'error', message: string} | null>(null);
  
  const languages = [
    { id: "english", name: "English", flag: "🇬🇧" },
    { id: "zulu", name: "isiZulu", flag: "🇿🇦" },
    { id: "afrikaans", name: "Afrikaans", flag: "🇿🇦" },
    { id: "xhosa", name: "isiXhosa", flag: "🇿🇦" },
    { id: "spanish", name: "Spanish", flag: "🇪🇸" }
  ];
  
  const saveSelection = () => {
    setToast({ type: 'success', message: 'Language updated successfully!' });
    setTimeout(() => navigate('/settings'), 1500);
  };
  
  return (
    <main className="pb-72 mx-auto w-full max-w-[480px]">
      <div className="overflow-hidden w-full bg-white">
        <MobileHeader />
        <section className="pt-8 pb-12 w-full">
          <header className="w-full text-xl font-semibold leading-none text-gray-900">
            <div className="px-4 w-full">
              <div className="w-full">
                <div className="flex justify-between items-center">
                  <h1 className="w-full flex items-center">
                    <button onClick={() => navigate('/settings')} className="mr-2">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    Language
                  </h1>
                </div>
              </div>
            </div>
          </header>

          <section className="mt-8 w-full">
            <div className="px-4 w-full">
              <p className="text-sm text-gray-600 mb-6">
                Choose your preferred language. This will change the language throughout the app.
              </p>
              
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-200 mb-8">
                {languages.map(language => (
                  <div 
                    key={language.id}
                    className="p-4 flex items-center cursor-pointer"
                    onClick={() => setSelectedLanguage(language.id)}
                  >
                    <div className="text-2xl mr-3">{language.flag}</div>
                    <div className="flex-1">
                      <h3 className="text-base font-medium text-gray-900">{language.name}</h3>
                    </div>
                    <div className="ml-4 flex-shrink-0">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${selectedLanguage === language.id ? 'border-blue-600' : 'border-gray-300'}`}>
                        {selectedLanguage === language.id && (
                          <div className="w-3 h-3 rounded-full bg-blue-600"></div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <button 
                onClick={saveSelection}
                className="w-full py-3 font-medium rounded-lg shadow-sm bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              >
                {selectedLanguage === "zulu" ? "Londoloza Ukhethe" : "Save Preference"}
              </button>
              
              {selectedLanguage === "zulu" && (
                <div className="mt-8 p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <h3 className="text-base font-medium text-blue-800 mb-2">IsiZulu Preview</h3>
                  <div className="space-y-2 text-sm text-blue-700">
                    <p>Sawubona, siyakwamukela ku-AquaSave</p>
                    <p>Amanzi alapha: <b>125L</b></p>
                    <p>Izilungiselelo &gt; Ulimi &gt; IsiZulu</p>
                  </div>
                </div>
              )}
            </div>
          </section>
        </section>
      </div>

      <TabBar />
      
      {/* Toast notification */}
      {toast && (
        <ToastNotification
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </main>
  );
};

const SettingsScreen: React.FC = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [notifications, setNotifications] = useState<NotificationSetting[]>([
    {
      id: "leaks",
      title: "Leak Alerts",
      description: "Get notified when a potential leak is detected",
      enabled: true
    },
    {
      id: "usage",
      title: "Usage Reports",
      description: "Weekly summary of your water usage",
      enabled: true
    },
    {
      id: "billing",
      title: "Billing Reminders",
      description: "Get notified before your bill is due",
      enabled: true
    },
    {
      id: "tips",
      title: "Water Saving Tips",
      description: "Receive personalized tips to reduce water usage",
      enabled: false
    },
    {
      id: "restrictions",
      title: "Water Restrictions",
      description: "Updates on local water restrictions",
      enabled: true
    }
  ]);
  
  const [language, setLanguage] = useState("English");
  const [unit, setUnit] = useState("Liters");
  
  const toggleNotification = (id: string) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === id 
          ? {...notification, enabled: !notification.enabled} 
          : notification
      )
    );
  };
  
  return (
    <main className="pb-72 mx-auto w-full max-w-[480px]">
      <div className="overflow-hidden w-full bg-white">
        <MobileHeader />
        <section className="pt-8 pb-12 w-full">
          <header className="w-full text-2xl font-semibold leading-none text-gray-900">
            <div className="px-4 w-full">
              <div className="w-full">
                <div className="flex flex-col justify-center w-full">
                  <h1 className="w-full">
                    Settings
                  </h1>
                </div>
              </div>
            </div>
          </header>

          <section className="mt-8 w-full">
            <div className="px-4 w-full">
              {/* Account Section */}
              <div className="mb-8">
                <h2 className="text-base font-semibold text-gray-900 mb-4">Account</h2>
                
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-200">
                  <Link to="/profile" className="p-4 flex justify-between items-center">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Profile Information</h3>
                      <p className="text-xs text-gray-500 mt-1">Update your personal details</p>
                    </div>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7 4L13 10L7 16" stroke="#667085" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </Link>
                  
                  <Link to="/password-security" className="block">
                    <div className="p-4 flex justify-between items-center">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">Password & Security</h3>
                        <p className="text-xs text-gray-500 mt-1">Manage your password and security settings</p>
                      </div>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 4L13 10L7 16" stroke="#667085" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </Link>
                  
                  <div className="p-4 flex justify-between items-center">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Connected Devices</h3>
                      <p className="text-xs text-gray-500 mt-1">Manage your water meters and smart devices</p>
                    </div>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7 4L13 10L7 16" stroke="#667085" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              </div>
              
              {/* Preferences Section */}
              <div className="mb-8">
                <h2 className="text-base font-semibold text-gray-900 mb-4">Preferences</h2>
                
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-200">
                  <div className="p-4 flex justify-between items-center">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Dark Mode</h3>
                      <p className="text-xs text-gray-500 mt-1">Enable dark theme for the app</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={darkMode} 
                        onChange={() => setDarkMode(!darkMode)} 
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  
                  <Link to="/language" className="block">
                    <div className="p-4 flex justify-between items-center">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">Language</h3>
                        <p className="text-xs text-gray-500 mt-1">Current: {language}</p>
                      </div>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 4L13 10L7 16" stroke="#667085" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </Link>
                  
                  <Link to="/measurement-units" className="block">
                    <div className="p-4 flex justify-between items-center">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">Measurement Units</h3>
                        <p className="text-xs text-gray-500 mt-1">Current: {unit}</p>
                      </div>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 4L13 10L7 16" stroke="#667085" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </Link>
                </div>
              </div>
              
              {/* Notifications Section */}
              <div className="mb-8">
                <h2 className="text-base font-semibold text-gray-900 mb-4">Notifications</h2>
                
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-200">
                  {notifications.map(notification => (
                    <div key={notification.id} className="p-4 flex justify-between items-center">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">{notification.title}</h3>
                        <p className="text-xs text-gray-500 mt-1">{notification.description}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={notification.enabled} 
                          onChange={() => toggleNotification(notification.id)} 
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Support Section */}
              <div className="mb-8">
                <h2 className="text-base font-semibold text-gray-900 mb-4">Support</h2>
                
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-200">
                  <div className="p-4 flex justify-between items-center">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Help Center</h3>
                      <p className="text-xs text-gray-500 mt-1">Get help with using the app</p>
                    </div>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7 4L13 10L7 16" stroke="#667085" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  
                  <div className="p-4 flex justify-between items-center">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Report an Issue</h3>
                      <p className="text-xs text-gray-500 mt-1">Let us know about any problems</p>
                    </div>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7 4L13 10L7 16" stroke="#667085" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  
                  <div className="p-4 flex justify-between items-center">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Contact Support</h3>
                      <p className="text-xs text-gray-500 mt-1">Speak to our customer service team</p>
                    </div>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7 4L13 10L7 16" stroke="#667085" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              </div>
              
              {/* About & Legal */}
              <div className="mb-8">
                <h2 className="text-base font-semibold text-gray-900 mb-4">About & Legal</h2>
                
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-200">
                  <div className="p-4 flex justify-between items-center">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">About AquaSave</h3>
                      <p className="text-xs text-gray-500 mt-1">Learn more about our service</p>
                    </div>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7 4L13 10L7 16" stroke="#667085" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  
                  <div className="p-4 flex justify-between items-center">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Privacy Policy</h3>
                      <p className="text-xs text-gray-500 mt-1">How we handle your data</p>
                    </div>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7 4L13 10L7 16" stroke="#667085" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  
                  <div className="p-4 flex justify-between items-center">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Terms of Service</h3>
                      <p className="text-xs text-gray-500 mt-1">Legal terms that apply to our service</p>
                    </div>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7 4L13 10L7 16" stroke="#667085" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              </div>
              
              {/* Logout Button */}
              <div className="mt-8">
                <button className="w-full py-3 text-sm font-medium text-red-600 bg-white rounded-lg border border-red-200 shadow-sm hover:bg-red-50 transition-colors">
                  Log Out
                </button>
                
                <p className="text-xs text-center text-gray-500 mt-4">
                  AquaSave App v2.0.3
                </p>
              </div>
            </div>
          </section>
        </section>
      </div>

      <TabBar />
    </main>
  );
};

export { 
  SettingsScreen as default, 
  MeasurementUnitsScreen, 
  LanguageScreen, 
  PasswordSecurityScreen 
};
